import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type { CategoryGroupEntity } from '@actual-app/core/types/models';
import type { TFunction } from 'i18next';

import { BotonCerrarHoja } from '#components/common/ModalHoja';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoNombreEmoji } from '#components/mobile/ui/CampoNombreEmoji';
import { separarEmoji, unirEmoji } from '#components/mobile/ui/emoji';
import { Hoja } from '#components/mobile/ui/Hoja';
import { HojaSeleccion } from '#components/mobile/ui/HojaSeleccion';
import { Icono } from '#components/mobile/ui/Icono';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, radio, texto } from '#components/mobile/ui/tokens';

import { nombreValido } from './hojasPlan';
import { motivoNuevaCategoria } from './nuevaCategoria';
import type { TipoCategoria } from './travesias';

type HojaNuevaCategoriaProps = {
  /** `grupo`: crea un grupo (sin selector de grupo). */
  modo: 'categoria' | 'grupo';
  grupos: CategoryGroupEntity[];
  /** Solo cuando se abre desde un grupo («Añadir categoría» en su menú). */
  grupoInicial?: CategoryGroupEntity['id'] | null;
  onCrear: (
    nombre: string,
    grupo: CategoryGroupEntity | null,
    tipo?: TipoCategoria,
  ) => void;
  /**
   * «+ Nuevo grupo» en el selector de grupo: crea el grupo y devuelve su id
   * (o `null` si no se pudo) para dejarlo elegido.
   */
  onCrearGrupo?: (nombre: string) => Promise<string | null>;
  /**
   * Editar en vez de crear («Editar categorías»): la misma hoja con el
   * nombre (y su emoji) ya puesto y «Guardar». En una categoría, «En el
   * grupo» también la cambia de grupo.
   */
  editar?: { nombre: string };
  /**
   * Tipo actual de la categoría (solo al editar una de gasto): «Normal»,
   * «Pago fijo» o «Travesía». El elegido llega a `onCrear`.
   */
  tipo?: TipoCategoria;
  /** Título y botón propios («Nueva travesía» desde Ahorros). */
  titulo?: string;
  textoBoton?: string;
  /**
   * La ayuda de Gavi la enseña rellena: sin teclado y «Crear» no hace nada
   * (Ahorros, «¿Cómo empiezo una travesía?»).
   */
  ejemplo?: {
    nombre: string;
    grupoId: string | null;
    eligiendo: boolean;
  };
  onCerrar: () => void;
};

/**
 * «Nueva categoría» / «Nuevo grupo» (B6 · H36): emoji (selector propio) y
 * nombre con el teclado ya abierto, «En el grupo: … ›» (abre la hoja de
 * selección de grupos, con «+ Nuevo grupo») y «Crear categoría» a todo el
 * ancho. Sin grupo propuesto: hasta elegirlo, el botón está desactivado y
 * se dice por qué (decisión del 6-oct-2026).
 */
export function HojaNuevaCategoria({
  modo,
  grupos,
  grupoInicial,
  onCrear,
  onCrearGrupo,
  editar,
  tipo,
  titulo: tituloPropio,
  textoBoton,
  ejemplo,
  onCerrar,
}: HojaNuevaCategoriaProps) {
  const { t } = useTranslation();
  const [tipoElegido, setTipoElegido] = useState<TipoCategoria | undefined>(
    tipo,
  );
  const [inicial] = useState(() =>
    editar || ejemplo
      ? separarEmoji((editar ?? ejemplo)?.nombre ?? '')
      : { emoji: null, resto: '' },
  );
  const [emoji, setEmoji] = useState<string | null>(inicial.emoji);
  const [nombre, setNombre] = useState(inicial.resto);
  const [grupoId, setGrupoId] = useState<string | null>(
    ejemplo ? ejemplo.grupoId : (grupoInicial ?? null),
  );
  // El grupo recién creado aún puede no estar en `grupos` (se recargan).
  const [grupoCreado, setGrupoCreado] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [eligiendo, setEligiendo] = useState(ejemplo?.eligiendo ?? false);
  // «+ Nuevo grupo»: su nombre se pide dentro de la hoja.
  const [nuevoGrupo, setNuevoGrupo] = useState<{
    emoji: string | null;
    nombre: string;
  } | null>(null);
  const [creandoGrupo, setCreandoGrupo] = useState(false);
  const grupo =
    grupos.find(g => g.id === grupoId) ??
    (grupoCreado && grupoCreado.id === grupoId
      ? ({
          ...grupoCreado,
          is_income: false,
        } as CategoryGroupEntity)
      : null);
  const esCategoria = modo === 'categoria';
  const motivo = motivoNuevaCategoria({
    modo,
    nombre,
    grupoId: grupo?.id ?? null,
  });
  const titulo =
    tituloPropio ??
    (editar
      ? esCategoria
        ? t('Edit category')
        : t('Edit category group')
      : esCategoria
        ? t('New category')
        : t('New category group'));

  const crear = () => {
    if (!motivo && !ejemplo) {
      const elegido = esCategoria ? grupo : null;
      if (tipoElegido) {
        onCrear(unirEmoji(emoji, nombre), elegido, tipoElegido);
      } else {
        onCrear(unirEmoji(emoji, nombre), elegido);
      }
    }
  };

  const crearGrupo = async () => {
    if (!nuevoGrupo || !onCrearGrupo || !nombreValido(nuevoGrupo.nombre)) {
      return;
    }
    const nombreGrupo = unirEmoji(nuevoGrupo.emoji, nuevoGrupo.nombre);
    setCreandoGrupo(true);
    const id = await onCrearGrupo(nombreGrupo);
    setCreandoGrupo(false);
    if (id) {
      setGrupoCreado({ id, name: nombreGrupo });
      setGrupoId(id);
      setNuevoGrupo(null);
    }
  };

  if (nuevoGrupo) {
    return (
      <Hoja
        aria-label={t('New category group')}
        data-testid="hoja-nueva-categoria"
        onCerrar={onCerrar}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Text
            role="heading"
            aria-level={2}
            style={{
              flex: 1,
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: color.fg,
            }}
          >
            <Trans>New category group</Trans>
          </Text>
          <BotonCerrarHoja onPress={onCerrar} />
        </View>
        <View style={{ marginBottom: 14 }}>
          <CampoNombreEmoji
            emoji={nuevoGrupo.emoji}
            onEmoji={e => setNuevoGrupo({ ...nuevoGrupo, emoji: e })}
            nombre={nuevoGrupo.nombre}
            onNombre={n => setNuevoGrupo({ ...nuevoGrupo, nombre: n })}
            aria-label={t('Group name')}
            placeholder={t('e.g. Home')}
            onEnter={() => void crearGrupo()}
            data-testid="nuevo-grupo-nombre"
          />
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Boton
            variante="fantasma"
            data-testid="nuevo-grupo-cancelar"
            onPress={() => setNuevoGrupo(null)}
          >
            <Trans>Cancel</Trans>
          </Boton>
          <Boton
            data-testid="nuevo-grupo-crear"
            isDisabled={!nombreValido(nuevoGrupo.nombre) || creandoGrupo}
            onPress={() => void crearGrupo()}
            style={{ flex: 1 }}
          >
            <Trans>Create group</Trans>
          </Boton>
        </View>
      </Hoja>
    );
  }

  return (
    <>
      <Hoja
        aria-label={titulo}
        data-testid="hoja-nueva-categoria"
        onCerrar={onCerrar}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Text
            role="heading"
            aria-level={2}
            style={{
              flex: 1,
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: color.fg,
            }}
          >
            {titulo}
          </Text>
          <BotonCerrarHoja onPress={onCerrar} />
        </View>
        <View style={{ marginBottom: 10 }}>
          <CampoNombreEmoji
            emoji={emoji}
            onEmoji={setEmoji}
            nombre={nombre}
            onNombre={setNombre}
            aria-label={esCategoria ? t('Category name') : t('Group name')}
            placeholder={esCategoria ? t('e.g. Groceries') : t('e.g. Home')}
            onEnter={crear}
            enfocar={!ejemplo}
            data-testid="nueva-nombre"
          />
        </View>
        {esCategoria && (
          <Button
            variant="bare"
            data-testid="nueva-grupo"
            aria-label={t('Change group')}
            onPress={() => setEligiendo(true)}
            style={{
              minHeight: 52,
              justifyContent: 'flex-start',
              gap: 8,
              padding: '0 14px',
              borderRadius: radio.boton,
              backgroundColor: color.surface2,
              color: color.fg,
              marginBottom: 14,
            }}
          >
            <Text style={{ ...texto.fila, color: color.fg2 }}>
              <Trans>In the group:</Trans>
            </Text>
            <Text
              style={{
                ...texto.fila,
                flex: 1,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {grupo?.name ?? t('Choose a group')}
            </Text>
            <Icono nombre="cr" size={18} style={{ color: color.fg3 }} />
          </Button>
        )}
        {esCategoria && tipo && tipoElegido && (
          <View style={{ marginBottom: 14, gap: 6 }}>
            <Text
              style={{ ...texto.secundario, fontWeight: 700, color: color.fg2 }}
            >
              <Trans>Kind of category</Trans>
            </Text>
            <Segmentado
              etiqueta={t('Kind of category')}
              testId="tipo-categoria"
              estirar
              alto={40}
              valor={tipoElegido}
              onChange={setTipoElegido}
              opciones={[
                { valor: 'normal', texto: t('Normal') },
                { valor: 'fijo', texto: t('Fixed payment') },
                { valor: 'travesia', texto: t('Voyage') },
              ]}
            />
            <Text
              data-testid="tipo-categoria-ayuda"
              style={{ ...texto.secundario, color: color.fg3 }}
            >
              {ayudaTipo(t, tipo, tipoElegido)}
            </Text>
          </View>
        )}
        {motivo === 'grupo' && (
          <Text
            data-testid="nueva-motivo"
            role="status"
            style={{
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: color.fg2,
              marginBottom: 8,
            }}
          >
            <Trans>Choose a group to create it</Trans>
          </Text>
        )}
        <Boton
          bloque
          data-testid="nueva-crear"
          isDisabled={!!motivo}
          onPress={crear}
          style={esCategoria ? undefined : { marginTop: 4 }}
        >
          {textoBoton ??
            (editar
              ? t('Save')
              : esCategoria
                ? t('Create category')
                : t('Create group'))}
        </Boton>
      </Hoja>
      {eligiendo && (
        <HojaSeleccion
          titulo={t('Choose a group')}
          textoBuscar={t('Search group')}
          secciones={[
            {
              id: 'grupos',
              titulo: t('Groups'),
              sinCabecera: true,
              filas: grupos.map(g => {
                const { emoji, resto } = separarEmoji(g.name);
                return { id: g.id, nombre: resto, emoji };
              }),
            },
          ]}
          onElegir={fila => {
            setGrupoId(fila.id);
            setEligiendo(false);
          }}
          filaFinal={
            onCrearGrupo
              ? {
                  nombre: t('New group'),
                  icono: <Icono nombre="plus" size={18} />,
                  accion: true,
                  onElegir: () => {
                    setEligiendo(false);
                    setNuevoGrupo({ emoji: null, nombre: '' });
                  },
                }
              : undefined
          }
          crear={
            onCrearGrupo
              ? {
                  etiqueta: escrito =>
                    t('Create group "{{name}}"', { name: escrito }),
                  onCrear: escrito => {
                    const partes = separarEmoji(escrito);
                    setEligiendo(false);
                    setNuevoGrupo({
                      emoji: partes.emoji,
                      nombre: partes.resto,
                    });
                  },
                }
              : undefined
          }
          onCerrar={() => setEligiendo(false)}
          enfocar={!ejemplo}
          data-testid="nueva-elegir-grupo"
        />
      )}
    </>
  );
}

/** Una línea bajo el selector de tipo: qué es y qué pasará al guardar. */
export function ayudaTipo(
  t: TFunction,
  antes: TipoCategoria,
  despues: TipoCategoria,
): string {
  if (despues === 'travesia') {
    return antes === 'travesia'
      ? t('A savings goal with a date and a letter for the day you arrive.')
      : t('When you save, choose the amount, the date and the letter.');
  }
  if (despues === 'fijo') {
    return t('A payment that repeats: rent, electricity, insurance…');
  }
  return antes === 'travesia'
    ? t('When you save, you choose what happens to the voyage letter.')
    : t('Spending without a fixed date or amount.');
}
